import * as _ from 'lodash';
import {DateTimePunchType, ErrorType, ForceCallEnum, IntervalPunchTypes, MealPenaltyEnum, UkPunchIntervalTypes} from './timecard.enum';
import {Splits} from './split-coding.model';
import {PayFactor, SplitBreakage, ValueError} from './timecard.model';
import {UkCrewWorkDetail} from './timecard-models/uk.model';

export interface ShiftDates {
  [shiftId: string]: ShiftDate;
}
export interface ShiftDate {
  shiftId: string;
  mobileTimeId?: string;
  dailyTimeId?: string;
  date: string; // date format: MM-DD-YY
  splitShiftOrdinal?: number;
}
export interface ShiftRates {
  [shiftId: string]: ShiftRate;
}
export interface ShiftRate {
  shiftId: string;
  jobCode: ValueError<string>;
  scheduleCode: ValueError<string>;
  rerateAmount: ValueError<number>;
  jobId?: string;
  scheduleId?: string;
}
export type WorkDetail = CrewDetail | SagDetail;
export type UKWorkDetail = UkCrewWorkDetail | SagDetail;

export interface WorkDetails {
  [shiftId: string]: WorkDetail;
}
export interface UkCrewWorkDetails {
  [shiftId: string]: UkCrewWorkDetail;
}
export interface CommonDetail {
  cityCode: ValueError<string>;
  countryCode: ValueError<string>;
  countyCode: ValueError<string>;
  dayType2: ValueError<string>;
  forceCall: ForceCallEnum;
  mealPenalty1: MealPenaltyEnum;
  mealPenalty2: MealPenaltyEnum;
  nightPremium: boolean;
  payType: ValueError<string>;
  shiftId: string;
  stateCode: ValueError<string>;
  workCondition: ValueError<string>;
}
export interface CrewDetail extends CommonDetail {
  dayType1: ValueError<string>;
  offProd: boolean;
  outZone: string;
}
export interface SagDetail extends CommonDetail {
  notPhotographed: boolean;
}
export type AccountCoding = AccountSplit | AccountCode;
export function IsSplitCoding(accountCoding: AccountCoding): accountCoding is AccountSplit {
  if (!accountCoding) {
    return true;
  }
  return (<AccountSplit>accountCoding).splits !== undefined;
}
export interface AccountCodes {
  [shiftId: string]: AccountCoding;
}
export interface AccountCode {
  shiftId: string;
  glShortcutId: string;
  segments: ValueError<string>[];
}
export interface AccountSplit {
  shiftId: string;
  splits: ValueError<Splits>;
  breakages?: SplitBreakage;
}
export interface Breakage {
  shiftId: string;
  hoursPaid: number;
  hoursWorked: number;
  payFactors?: PayFactor[];
  totalAmount: number;
  MP1Count: number;
  MP1Amount: number;
  MP2Count: number;
  MP2Amount: number;
  isRestViolation: boolean;
  originalHoursPaid?: number;
  originalTotalAmount?: number;
}
export interface Breakages {
  [shiftId: string]: Breakage;
}
export type UkPunchIntervals = {
  [key in keyof typeof UkPunchIntervalTypes]?: ValueError<number>;
};

/**
 * IPunch<PunchValueType> - the Generic form of Punch interface which represents a list of punches for a shift.
 * All punches across all region will have the Key as PunchType and the Punch Value as a ValueError of a generic Punch Value Type.  This PunchValueType can be
 *   anything from just a number, e.g. UsaPunch to an region specific interface, e.g. GbrPunch
 *
 * If we are recording the Punch for a USA timecard, then we will use the UsaPunch type, which is just a number.  If it is for a different region, e.g. GBR,
 * then we will use the GbrPunch, which is am object.
 * example
 * const exampleUsaPunch: IRegionPunch<UsaPunch> = {
 *   call: {
 *     value: 12.7
 *   }
 * };
 * TODO: notice how lame it would be if we need to store the display value, we cannot just do do (new Date()).toString() because it maybe a few microsecond
 *  different than the punchValue.  We'll have to initialize this with a temp var to store the new Date().  This is not nice to have to deal with constantly.
 *  I think we should just use a Pipe in the UI to parse the date into the GBR punch format, e.g. 18:42.
 *  *Note* The pipe will also need to know the Shift Date as a param to support the next day use case if we are storing punch as a Date.
 *         However, if we store GBR Punch as punchHr and punchMin then we don't need shift date.
 * const exampleGbrPunch: IRegionPunch<GbrPunch> = {
 *   call: {
 *     value: {
 *       punchValue: new Date(),
 *       displayValue: ''
 *     }
 *   }
 * };
 * */
export type IPunch<PunchValueType> = {
  [key in keyof typeof DateTimePunchType]?: ValueError<PunchValueType>;
};
export type IIntervalPunch = {
  [key in keyof typeof IntervalPunchTypes]?: ValueError<number>;
};
// Region Specific Implementation of the Generic Punch
export type UsaPunchValueType = number;
/**
 * UK Punch Value Type
 * We are using a string Date with format: StandardDateTimeFormatString to store UK Punch Values.  This is chosen because it is a format that we know
 * do not contain time zone info.  We use this format simply because we don't want to risk accidentally adding timezone info into a Punch, which will cause
 * the punch to be recorded incorrectly.
 * */
export type UkPunchValueType = string;
/**
 * Note - I've renamed existing IPunch interface to UsaPunch, then I created the Generic Punch - IRegionPunch<P>, Then, I create the Alias for the generic
 *        eqv of the old IPunch.
 * example
 * const ukPunch: IUkPunch = {
 *   call: { value: '30-05-2024 9:00:00' },
 *   wrap: { value: '30-05-2024 20:00:00'}
 * };
 * */
export type IUsaPunch = IPunch<UsaPunchValueType>;
export type IUkPunch = IPunch<UkPunchValueType>;

/**
 * Punch<PunchValueType> - TODO - We should call this ShiftPunch
 * This is basically just IPunch<PunchValueType> but we also need the ShiftId in it, but doesn't want to require shiftId for any Punch object.
 * */
export interface Punch<PunchValueType> extends IPunch<PunchValueType>, IIntervalPunch {
  shiftId?: string;
}
/**
 * Punches<PunchValueType>
 * This is ShiftPunches that is used directly in Shifts<PunchValueType>
 * */
export interface Punches<P> {
  [shiftId: string]: Punch<P>;
}
/**
 * Combined punch type with direct values (not wrapped in ValueError)
 * Uses string values for DateTimePunchType keys and number values for IntervalPunchTypes keys
 */
export type UnitPunch = {
  [key in keyof typeof DateTimePunchType]?: string;
} & {
  [key in keyof typeof IntervalPunchTypes]?: number;
};
/**
 * UsaPunch and UsaPunches - these are the Concrete, Region Specific type of Punch and Punches.  This is equivalent to the old Punch/Punches interface.
 * TODO: This is the USA Punch, because we use this interface EVERY-FREAKING-WHERE, I am incline to change them all at once.  Because the way we setup
 *   the IRegionPunch, RPunch<UsaPunch> turns out to be exactly the same as the old Punch interface:
 *   export interface Punch extends IPunch {
 *     shiftId?: string;
 *   }
 *   For "Phase 0" we will leave Punch as an alias so that we don't disturb anything outside of Timecard/Shifts interface that uses Punch.  Once we proven
 *   the generic interface Timecard<P> works, we'll refactor everything else.
 * */
export type UkPunch = Punch<UkPunchValueType>;
export type UsaPunch = Punch<UsaPunchValueType>;
export interface UsaPunches {
  [shiftId: string]: UsaPunch;
}
export interface UkPunches {
  [shiftId: string]: UkPunch;
}
export type UsaPunchValues = { shiftId?: string; } & {
  [K in keyof Omit<UsaPunch, 'last' | 'shiftId'>]?: number;
};
export type UkPunchValues = { shiftId?: string; } & {
  [K in keyof Omit<UsaPunch, 'last' | 'shiftId'>]?: number;
};
export type DetailValues = { shiftId?: string; } & {
  [K in keyof Pick<CrewDetail, 'dayType1' | 'dayType2' | 'payType'>]?: string;
};
// TODO: Find a better home for SAG only fields like WorkStatuses and StuntAdjustments.
//  If we are putting them in a separate object, we should name it properly.
//  Should we use this new object in Shifts to accommodate all the unique fields for a specific type of timecard?
//  If that is the approach, then we should give it a more appropriate name
export interface WorkStatus {
  shiftId: string;
  workStatuses?: ValueError<ValueError<string>[]>;
  // TODO: HACK - Just adding stuntAdjustment here as these are all SAG specific field and we do not want to change Crew code as much as possible
  stuntAdjustment?: ValueError<number>;
}
export interface WorkStatuses {
  [shiftId: string]: WorkStatus;
}



export const FindShiftIdByWorkDate = (shiftDates: ShiftDates, workDate: string): string => _
  .chain(shiftDates)
  .values()
  .filter(({date}) => date === workDate)
  .map('shiftId')
  .head()
  .value();
export const IsSagDetail = (obj: any): obj is SagDetail =>
  _.isBoolean(obj?.notPhotographed);
export const GetSagDetail = (workDetail: Omit<WorkDetail, 'shiftId'>): SagDetail => {
  if (IsSagDetail(workDetail)) {
    return workDetail;
  }
  throw new Error(`Trying to use Crew Detail as SagDetail`);
};
export const IsCrewDetail = (obj: any): obj is CrewDetail =>
  _.isString(obj?.dayType1?.value) || _.values(ErrorType).includes(obj?.errorType);

export const IsUKCrewDetail = (obj: any): obj is UkCrewWorkDetail =>
  _.isString(obj?.dayType1?.value) || _.values(ErrorType).includes(obj?.errorType);
export const GetCrewDetail = (workDetail: Omit<WorkDetail, 'shiftId'>): CrewDetail => {
  if (IsCrewDetail(workDetail)) {
    return workDetail;
  }
  throw new Error(`Trying to use SagDetails as CrewDetail`);
};
export const GetUKCrewDetail = (workDetail: Omit<UKWorkDetail, 'shiftId'>): UkCrewWorkDetail => {
  if (IsUKCrewDetail(workDetail)) {
    return workDetail;
  }
  throw new Error(`Trying to use SagDetails as UkCrewWorkDetail`);
};
export interface IShifts<P> {
  shiftDates: ShiftDates;
  shiftRates?: ShiftRates;
  workDetails: WorkDetails;
  accountCodes?: AccountCodes;
  breakages?: Breakages;
  payPunches?: Punches<P>;
  prPunches?: Punches<P>;
  mobilePunches?: Punches<P>;
  workStatuses?: WorkStatuses;
}

/**
 * Concrete-Region Specific Shifts Types
 * */
export type UsaShifts = IShifts<UsaPunchValueType>;

export const IsEmptyPunch = <T>(payPunch: Punch<T>): boolean => _
  .chain(payPunch)
  .omit('shiftId')
  .every((punch) => {
    if (_.isNull(_.get(punch, 'value', null))) {
      return true;
    }
    return _.isEmpty(punch);
  })
  .value();

export const IsEmptyUkPunch = IsEmptyPunch<UkPunchValueType>;
export const IsEmptyUsPunch = IsEmptyPunch<UsaPunchValueType>;
